function prepareMemorialQuoteThumbnails(root) {
  if (root.dataset.productId !== '12679566754110') return;
  root.querySelectorAll('[data-bonaper-field="quote"] img.bonaper-swatch__img').forEach(image => {
    if (image.dataset.memorialQuoteReady) return;
    image.dataset.memorialQuoteReady = 'true';
    // Display-only swatches do not need canvas CORS or lazy layout scheduling.
    image.removeAttribute('crossorigin');
    image.loading = 'eager';
    image.decoding = 'async';
    image.width = 48;
    image.height = 48;
    const retry = () => {
      if (image.dataset.memorialQuoteRetried) return;
      image.dataset.memorialQuoteRetried = 'true';
      const source = new URL(image.src, document.baseURI);
      source.searchParams.set('quote_thumbnail_retry', '1');
      image.src = source.href;
    };
    image.addEventListener('error', retry, { once: true });
    if (image.complete && !image.naturalWidth) retry();
  });
}

/** Use Kaching's displayed deal total; Shopify still owns cart pricing. */
function getMemorialBundlePrice(productInfo, fallbackPrice) {
  if (productInfo.dataset.productId !== '12679566754110') return fallbackPrice;
  const bundle = productInfo.querySelector('kaching-bundles-block');
  const selectedPrice = bundle?.querySelector('.kaching-bundles__bar--selected .kaching-bundles__bar-price');
  const displayedPrice = selectedPrice?.textContent.trim();
  if (!displayedPrice) return fallbackPrice;

  try {
    const config = JSON.parse(bundle.getAttribute('config') || '{}');
    const deal = JSON.parse(bundle.getAttribute('deal-block') || '{}');
    const currency = config.marketCurrencyCode || fallbackPrice?.match(/\b[A-Z]{3}\b/u)?.[0];
    const additions = deal.includeUpsellsInDealBarPrice ? [] : [...bundle.querySelectorAll(
      '.kaching-bundles__checkbox-upsells__upsell [role="checkbox"][aria-checked="true"]'
    )];
    if (!additions.length) {
      return currency && !displayedPrice.includes(currency) ? `${displayedPrice} ${currency}` : displayedPrice;
    }
    if (!currency) return fallbackPrice;

    const formatter = new Intl.NumberFormat(config.liquidLocale || config.locale || 'en', { style: 'currency', currency });
    const parts = formatter.formatToParts(12345.6);
    const group = parts.find(part => part.type === 'group')?.value;
    const decimal = parts.find(part => part.type === 'decimal')?.value;
    const minorDigits = formatter.resolvedOptions().maximumFractionDigits;
    const scale = 10 ** minorDigits;
    const amounts = [displayedPrice, ...additions.map(addition => addition.querySelector('.kaching-bundles__bar-price')?.textContent.trim())];
    let total = 0;
    for (const amount of amounts) {
      if (!amount) return fallbackPrice;
      let normalized = group ? amount.replaceAll(group, '') : amount;
      if (decimal) normalized = normalized.replace(decimal, '.');
      normalized = normalized.replace(/[^\d.-]/g, '');
      if (!normalized || !Number.isFinite(Number(normalized))) return fallbackPrice;
      total += Math.round(Number(normalized) * scale);
    }
    const formatted = formatter.format(total / scale);
    return formatted.includes(currency) ? formatted : `${formatted} ${currency}`;
  } catch {
    return fallbackPrice;
  }
}

if (!customElements.get('memorial-reviews')) {
  customElements.define('memorial-reviews', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.cards = this.querySelector('[data-review-cards]');
      this.status = this.querySelector('[role="status"]');
      this.more = this.querySelector('button');
      this.more.addEventListener('click', () => this.load(this.nextPage), { signal: this.controller.signal });
      this.load(1);
    }

    disconnectedCallback() {
      this.controller.abort();
    }

    async load(page) {
      if (this.loading || !Number.isSafeInteger(page) || page < 1) return;
      this.loading = true;
      this.more.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Loading customer reviews…';
      try {
        const response = await fetch(`/apps/bonatrust/widget/${encodeURIComponent(this.dataset.handle)}?page=${page}&sort=recent`, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Reviews unavailable');
        const fragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = fragment.querySelector('.bt-widget');
        if (!root || root.querySelector('.bt-title')?.textContent.trim() !== 'Reviews for this item') throw new Error('Product reviews unavailable');
        // Preserve BonaTrust's published text and ratings; never inject its HTML or scripts.
        const reviews = [...root.querySelectorAll('.bt-card')].filter(card => !card.querySelector('.bt-badge-store'));
        const added = document.createDocumentFragment();
        for (const review of reviews) {
          const card = document.createElement('article');
          card.className = 'memorial-reviews__card';
          const stars = document.createElement('div');
          const rating = Math.min(5, Math.max(0, Number(review.querySelector('.bt-num')?.textContent) || 0));
          stars.className = 'memorial-reviews__stars';
          stars.setAttribute('role', 'img');
          stars.setAttribute('aria-label', `${rating} out of 5 stars`);
          stars.textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating);
          card.append(stars);
          const title = review.querySelector('.bt-card-title')?.textContent.trim();
          if (title) {
            const heading = document.createElement('h3');
            heading.className = 'memorial-reviews__title';
            heading.textContent = title;
            card.append(heading);
          }
          const body = document.createElement('p');
          body.className = 'memorial-reviews__body';
          body.textContent = review.querySelector('.bt-card-body')?.textContent.trim() || '';
          const author = document.createElement('p');
          author.className = 'memorial-reviews__author';
          author.textContent = review.querySelector('.bt-head-right b')?.textContent.trim() || 'Customer';
          const date = document.createElement('time');
          date.className = 'memorial-reviews__date';
          date.textContent = review.querySelector('time')?.textContent.trim() || '';
          card.append(body, author, date);
          added.append(card);
        }
        this.cards.append(added);
        this.nextPage = Number(root.querySelector('[data-bt-page]')?.dataset.btPage) || null;
        this.more.hidden = !this.nextPage;
        const average = root.querySelector('.bt-avg')?.textContent.trim();
        const count = root.querySelector('.bt-avg-meta')?.textContent.match(/\((\d+) reviews\)/)?.[1];
        this.status.textContent = reviews.length ? `${average} out of 5 · ${count || this.cards.children.length} reviews · BonaTrust` : 'No customer reviews yet.';
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.status.textContent = 'Customer reviews could not be loaded. Please try again.';
        this.nextPage = page;
        this.more.hidden = false;
      } finally {
        this.loading = false;
        this.more.disabled = false;
        this.setAttribute('aria-busy', 'false');
      }
    }
  });
}

/* The dock delegates to Dawn/BonaPer's original submit button. */
if (!customElements.get('memorial-product-page')) {
  customElements.define('memorial-product-page', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.productInfo = this.querySelector('product-info');
      this.dock = this.querySelector('[data-memorial-dock]');
      this.dockButton = this.querySelector('[data-memorial-add]');
      const data = this.querySelector('[data-memorial-variants]');
      if (!this.productInfo || !this.dock || !this.dockButton || !data) return;
      this.variants = JSON.parse(data.textContent).variants;
      this.dockButton.addEventListener('click', () => {
        const submit = this.productInfo.querySelector('.product-form__submit');
        if (!submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
        // .click() preserves BonaPer's validation, customization save and properties.
        submit.click();
        const error = this.productInfo.querySelector('[data-bonaper-field-error]:not([hidden])');
        if (error) error.scrollIntoView({ block: 'center', behavior: 'auto' });
      }, { signal: this.controller.signal });
      this.productInfo.addEventListener('change', () => this.sync(), { signal: this.controller.signal });
      this.productInfo.addEventListener('input', () => this.sync(), { signal: this.controller.signal });
      this.productInfo.addEventListener('submit', event => {
        if (!event.target.matches('form[data-type="add-to-cart-form"]')) return;
        this.bindPersonalizationGuard();
        if (this.guardedForm === event.target) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.productInfo.querySelector('product-form')?.handleErrorMessage(this.dataset.saveError);
      }, { capture: true, signal: this.controller.signal });
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.productInfo, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'value', 'aria-busy', 'class', 'aria-checked'] });
      this.intersection = new IntersectionObserver(([entry]) => {
        this.dock.hidden = entry.boundingClientRect.bottom >= 0;
      });
      window.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateDockVisibility();
        });
      }, { passive: true, signal: this.controller.signal });
      const button = this.productInfo.querySelector('.product-form__submit');
      if (button) this.intersection.observe(button);
      this.sync();
    }

    sync() {
      prepareMemorialQuoteThumbnails(this.productInfo);
      this.bindPersonalizationGuard();
      this.updateDockVisibility();
      const submit = this.productInfo.querySelector('.product-form__submit');
      const id = this.productInfo.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value;
      const variant = this.variants.find(item => String(item.id) === id);
      const price = getMemorialBundlePrice(this.productInfo, variant?.price);
      this.dockButton.disabled = !submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true';
      const submitLabel = submit?.querySelector('span');
      const addLabel = this.dataset.addLabel;
      // Dawn replaces this label after a variant change. Reapply the actual
      // selected price while preserving sold-out, unavailable and busy states.
      if (variant && submitLabel && !submit.disabled && addLabel && submitLabel.textContent.trim().startsWith(addLabel)) {
        const pricedLabel = `${addLabel} — ${price}`;
        if (submitLabel.textContent !== pricedLabel) submitLabel.textContent = pricedLabel;
      }
      const label = submitLabel?.textContent.trim();
      if (label && this.dockButton.textContent !== label) this.dockButton.textContent = label;
      if (!variant) return;
      const dockPrice = this.querySelector('[data-memorial-price]');
      if (dockPrice.textContent !== price) dockPrice.textContent = price;
      this.querySelector('[data-memorial-variant]').textContent = variant.title;
    }

    updateDockVisibility() {
      const submit = this.productInfo.querySelector('.product-form__submit');
      this.dock.hidden = !submit || submit.getBoundingClientRect().bottom >= 0;
    }

    bindPersonalizationGuard() {
      const fields = this.productInfo.querySelector('[data-bonaper-fields]');
      const form = this.productInfo.querySelector('form[data-type="add-to-cart-form"]');
      if (!form || this.guardedForm === form || fields?.getAttribute('aria-busy') !== 'false') return;
      this.guardedForm = form;
      // Bind after BonaPer has initialized its form capture handler. Its first
      // event is canceled while saving; a successful replay carries both keys.
      form.addEventListener('submit', event => {
        if (event.defaultPrevented) return;
        const id = form.querySelector('[name="properties[_bonaper_id]"]')?.value;
        const version = form.querySelector('[name="properties[_bonaper_version]"]')?.value;
        if (id && /^[1-9]\d*$/.test(version || '')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const error = this.productInfo.querySelector('product-form');
        error?.handleErrorMessage(this.dataset.saveError);
      }, { capture: true, signal: this.controller.signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.intersection?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }
  });
}

if (!customElements.get('memorial-finalize')) {
  customElements.define('memorial-finalize', class extends HTMLElement {
    connectedCallback() {
      this.main = document.querySelector('memorial-product-page');
      this.info = this.main?.querySelector('product-info');
      if (!this.info) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.status = this.querySelector('[data-finalize-status]');
      this.cartLink = this.querySelector('[data-finalize-cart]');
      this.addEventListener('input', event => {
        const token = event.target.dataset.finalizeField;
        if (!token) return;
        const original = this.info.querySelector(`input[id$="-${token}"]`);
        if (!original) return;
        original.value = event.target.value;
        original.dispatchEvent(new Event('input', { bubbles: true }));
        original.dispatchEvent(new Event('change', { bubbles: true }));
        if (token === 'petName' && event.target.value.trim()) this.showNameError(false);
        this.status.textContent = '';
        this.cartLink.hidden = true;
      }, options);
      this.addEventListener('click', event => {
        const size = event.target.closest('[data-finalize-variant]');
        if (size) {
          if (size.getAttribute('aria-pressed') === 'true' || this.pendingVariant || this.pendingAdd) return;
          const variants = JSON.parse(this.main.querySelector('[data-memorial-variants]').textContent).variants;
          const index = variants.findIndex(v => String(v.id) === size.dataset.finalizeVariant);
          const original = this.info.querySelectorAll('variant-selects input[type="radio"]')[index];
          if (!original) return;
          this.pendingVariant = size.dataset.finalizeVariant;
          this.variantViewport = window.scrollY;
          this.status.textContent = 'Updating your size…';
          this.cartLink.hidden = true;
          original.click();
          this.sync();
        }
        if (event.target.closest('[data-finalize-photo]')) {
          // Open the existing BonaPer chooser in this user gesture. Its own
          // change handler still performs upload, cropping and preview work.
          this.info.querySelector('[data-bonaper-field="photo"] input[type="file"]')?.click();
        }
        if (event.target.closest('[data-finalize-add]')) {
          const submit = this.info.querySelector('.product-form__submit');
          if (this.pendingAdd || this.pendingVariant || !submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
          const name = this.querySelector('[data-finalize-field="petName"]');
          if (!name.value.trim()) {
            this.showNameError(true);
            name.focus({ preventScroll: true });
            return;
          }
          this.pendingAdd = this.info.querySelector('form[data-type="add-to-cart-form"] [name="id"]')?.value;
          this.status.textContent = 'Saving your personalization…';
          this.cartLink.hidden = true;
          this.querySelector('[data-finalize-add]').disabled = true;
          submit.click();
          this.sync();
        }
      }, options);
      this.main.addEventListener('input', () => this.sync(), options);
      this.main.addEventListener('change', () => this.sync(), options);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (!this.pendingAdd || String(event.productVariantId) !== this.pendingAdd || event.source !== 'product-form') return;
          this.pendingAdd = null;
          this.status.textContent = 'Added to your cart.';
          this.cartLink.hidden = false;
          this.sync();
        });
        this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, event => {
          if (!this.pendingAdd || String(event.productVariantId) !== this.pendingAdd) return;
          this.pendingAdd = null;
          this.status.textContent = typeof event.errors === 'string' ? event.errors : 'Unable to add this item. Please try again.';
          this.sync();
        });
      }
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.main, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'value', 'hidden'] });
      this.sync();
    }

    showNameError(show) {
      this.querySelector('[data-finalize-name-error]').hidden = !show;
      this.querySelector('[data-finalize-field="petName"]').setAttribute('aria-invalid', String(show));
    }

    sync() {
      this.info = this.main.querySelector('product-info');
      if (!this.info) return;
      const id = this.info.querySelector('form[data-type="add-to-cart-form"] [name="id"]')?.value;
      const submit = this.info.querySelector('.product-form__submit');
      if (this.pendingVariant && this.pendingVariant === id && submit && !submit.disabled) {
        const selected = this.querySelector(`[data-finalize-variant="${id}"]`);
        this.pendingVariant = null;
        this.status.textContent = '';
        // Dawn focuses the native option after its response. Keep users at
        // the closing card when that request originated from this card.
        selected?.focus({ preventScroll: true });
        window.scrollTo({ top: this.variantViewport, behavior: 'instant' });
      }
      this.querySelectorAll('[data-finalize-variant]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.finalizeVariant === id));
      });
      for (const mirror of this.querySelectorAll('[data-finalize-field]')) {
        const original = this.info.querySelector(`input[id$="-${mirror.dataset.finalizeField}"]`);
        mirror.disabled = !original;
        if (original && document.activeElement !== mirror) mirror.value = original.value;
      }
      const ready = this.info.querySelector('[data-bonaper-fields]')?.getAttribute('aria-busy') === 'false' && !!this.info.querySelector('input[id$="-petName"]');
      const issue = this.info.querySelector('[data-bonaper-field-error]:not([hidden]), .product-form__error-message-wrapper:not([hidden])');
      if (this.pendingAdd && issue?.textContent.trim()) {
        this.pendingAdd = null;
        this.status.textContent = issue.textContent.trim();
      }
      this.querySelector('[data-finalize-add]').disabled = !ready || !!this.pendingAdd || !!this.pendingVariant || !submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true';
      this.querySelector('[data-finalize-photo]').disabled = !ready || !!this.pendingAdd;
      this.setAttribute('aria-busy', String(!!this.pendingAdd || !!this.pendingVariant));
      const price = this.main.querySelector('[data-memorial-price]')?.textContent;
      const priceLabel = this.querySelector('[data-finalize-price]');
      if (price && priceLabel.textContent !== price) priceLabel.textContent = price;
      const fileName = this.info.querySelector('[data-bonaper-field="photo"] input[type="file"]')?.files?.[0]?.name;
      this.querySelector('[data-finalize-photo]').textContent = fileName ? `Change photo — ${fileName}` : 'Upload Your Favorite Photo';
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.unsubscribeCart?.();
      this.unsubscribeError?.();
    }
  });
}
